import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { AppBar, Toolbar, Button, Avatar, Menu, MenuItem, IconButton, useTheme, Box, Typography, Divider, Drawer, List, ListItem } from '@mui/material';
import { Menu as MenuIcon, ExitToApp, Dashboard, LocalShipping, Build, CalendarMonth, Inventory, Inventory2, Person, Help as HelpIcon, FindInPage, AccountTree, Assignment, Description } from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { styled, alpha } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import './Navbar.css';
import { brand } from '../../theme';

const TRACE_BRAND_NAVY = brand.ink;
const TRACE_BRAND_SKY = brand.sky;

const DRAWER_WIDTH = 260;

const StyledAppBar = styled(AppBar)(({ theme, open }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(12px)',
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1300,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: `${DRAWER_WIDTH}px`,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  '& .MuiToolbar-root': {
    padding: '0 24px',
    minHeight: '60px',
  },
  [theme.breakpoints.down('md')]: {
    ...(open && {
      marginLeft: 0,
      width: '100%',
    }),
  },
}));


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
  backgroundColor: TRACE_BRAND_NAVY,
}));

const globalStyles = {
  'button:focus, button:active, .MuiButtonBase-root:focus, .MuiButtonBase-root:active': {
    outline: 'none !important',
    boxShadow: 'none !important',
  },
};

const NavButton = styled(Button)(({ theme }) => ({
  margin: '0',
  padding: '8px 12px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.82)',
  position: 'relative',
  border: '1px solid transparent',
  transition: `background-color 160ms ${brand.ease}, border-color 160ms ${brand.ease}, color 160ms ${brand.ease}`,
  '&:hover, &:focus, &:active': {
    backgroundColor: alpha(TRACE_BRAND_SKY, 0.14),
    borderColor: alpha(TRACE_BRAND_SKY, 0.26),
    color: brand.white,
    outline: 'none',
    boxShadow: 'none',
  },
  '&.active': {
    color: brand.white,
    backgroundColor: alpha(TRACE_BRAND_SKY, 0.2),
    borderColor: alpha(TRACE_BRAND_SKY, 0.42),
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '8px',
      bottom: '8px',
      width: '3px',
      borderRadius: '999px',
      backgroundColor: TRACE_BRAND_SKY,
    }
  },
  '&.Mui-focusVisible, &:focus-visible': {
    outline: 'none',
    boxShadow: 'none',
  },
  '&.MuiButtonBase-root:active': {
    boxShadow: 'none',
  },
  '&.MuiButton-root': {
    '&:focus, &:active': {
      backgroundColor: alpha(TRACE_BRAND_SKY, 0.14),
      outline: 'none',
      boxShadow: 'none',
    },
    '&.Mui-focusVisible': {
      backgroundColor: alpha(TRACE_BRAND_SKY, 0.14),
      '&:after': {
        display: 'none' // Hide focus indicator for keyboard navigation
      }
    }
  }
}));

const UserMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    minWidth: '200px',
    borderRadius: '8px',
    boxShadow: '0 12px 28px rgba(11, 32, 56, 0.14)',
    marginTop: '8px',
  },
}));


const NAV_SECTIONS = [
  {
    titleKey: 'nav.sections.core',
    items: [
      {
        translationKey: 'nav.dashboard',
        to: '/dashboard',
        icon: <Dashboard fontSize="small" />,
        allowedRoles: ['admin', 'quality', 'planner', 'aankoop']
      },
      {
        translationKey: 'nav.incomingDeliveries',
        to: '/incoming-deliveries',
        icon: <LocalShipping fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.productionRecipes',
        to: '/production-recipes',
        icon: <Build fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.pallets',
        to: '/pallets',
        icon: <Inventory fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.lotTrace',
        to: '/lot-trace',
        icon: <AccountTree fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.traceTestAuto',
        to: '/trace-test-auto',
        icon: <FindInPage fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.recalls',
        to: '/recalls',
        icon: <Description fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.compliance',
        to: '/compliance',
        icon: <Assignment fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      }
    ]
  },
  {
    titleKey: 'nav.sections.support',
    items: [
      {
        translationKey: 'nav.audits',
        to: '/audits',
        icon: <Assignment fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.incomingStock',
        to: '/incoming-deliveries/stock',
        icon: <Inventory2 fontSize="small" />,
        allowedRoles: ['admin', 'quality']
      },
      {
        translationKey: 'nav.supplierDocuments',
        to: '/supplier-documents',
        icon: <Description fontSize="small" />,
        allowedRoles: ['admin', 'quality', 'aankoop']
      },
      {
        translationKey: 'nav.processes',
        to: '/processes',
        icon: <AccountTree fontSize="small" />,
        allowedRoles: ['admin', 'quality', 'planner', 'aankoop']
      }
    ]
  },
  {
    titleKey: 'nav.sections.planning',
    items: [
      {
        translationKey: 'nav.productionOrders',
        to: '/production-orders',
        icon: <Build fontSize="small" />,
        allowedRoles: ['admin', 'planner']
      },
      {
        translationKey: 'nav.productionPlanning',
        to: '/production-planning',
        icon: <CalendarMonth fontSize="small" />,
        allowedRoles: ['admin', 'planner']
      }
    ]
  },
  {
    titleKey: 'nav.administration',
    adminOnly: true,
    items: [
      {
        translationKey: 'nav.masterData',
        to: '/administration/master-data',
        icon: <Inventory2 fontSize="small" />,
        allowedRoles: ['admin']
      },
      {
        translationKey: 'nav.users',
        to: '/administration/users',
        icon: <Person fontSize="small" />,
        allowedRoles: ['admin']
      }
    ]
  }
];

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { sidebarOpen, toggleSidebar, setSidebarOpen, isMobile } = useSidebar();
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();

  // Close menus when navigating (keep sidebar open)
  useEffect(() => {
    setAnchorEl(null);
    if (isMobile) {
      setSidebarOpen(false); // Close sidebar on mobile after navigation
    }
  }, [location.pathname, isMobile, setSidebarOpen]);

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    toggleSidebar();
  };

  // User menu (avatar) handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Logout
  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
    } finally {
      navigate('/login');
    }
  };

  const isNavItemActive = (item) => (
    item.to === '/dashboard'
      ? location.pathname === item.to
      : location.pathname === item.to ||
        (item.to === '/incoming-deliveries'
          ? location.pathname.startsWith('/incoming-deliveries/') && !location.pathname.startsWith('/incoming-deliveries/stock')
          : location.pathname.startsWith(`${item.to}/`))
  );

  const visibleSections = user
    ? NAV_SECTIONS
      .filter((section) => !section.adminOnly || user.role === 'admin')
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.allowedRoles.includes(user.role)),
      }))
      .filter((section) => section.items.length > 0)
    : [];

  return (
    <>
      <GlobalStyles styles={globalStyles} />
      
      {/* Sidebar Navigation */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={sidebarOpen}
        onClose={handleSidebarToggle}
        sx={{
          width: 0, // Container neemt geen ruimte in
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${alpha(TRACE_BRAND_SKY, 0.22)}`,
            background: TRACE_BRAND_NAVY,
            color: brand.white,
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            overflowY: 'auto',
            transition: theme.transitions.create('transform', {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
            transform: sidebarOpen && !isMobile ? 'translateX(0)' : 'translateX(-100%)',
          },
        }}
      >
        <DrawerHeader>
          <Link to="/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box component="img" src="/images/TRACE.svg" alt="Trace" sx={{ height: 32, borderRadius: '8px' }} />
            <Typography
              sx={{
                ml: 1.25,
                color: alpha(TRACE_BRAND_SKY, 0.95),
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                lineHeight: 1,
                textTransform: 'uppercase',
              }}
            >
              by Appetite
            </Typography>
          </Link>
        </DrawerHeader>
        <Divider sx={{ borderColor: alpha(TRACE_BRAND_SKY, 0.18) }} />
        {visibleSections.map((section, sectionIndex) => (
          <Box key={section.titleKey}>
            {sectionIndex > 0 && <Divider sx={{ my: 0.75, borderColor: alpha(TRACE_BRAND_SKY, 0.18) }} />}
            <Box sx={{ px: 2, pt: sectionIndex === 0 ? 1.25 : 0.75, pb: 0.5 }}>
              <Typography variant="overline" sx={{ color: alpha(TRACE_BRAND_SKY, 0.95), fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.2 }}>
                {t(section.titleKey)}
              </Typography>
            </Box>
            <List sx={{ px: 1, py: 0 }}>
              {section.items.map((item) => {
                const active = isNavItemActive(item);
                return (
                  <ListItem
                    key={item.to}
                    disablePadding
                    sx={{ mb: 0.25 }}
                  >
                    <NavButton
                      component={Link}
                      to={item.to}
                      startIcon={item.icon}
                      fullWidth
                      size="small"
                      className={active ? 'active' : ''}
                      sx={{
                        justifyContent: 'flex-start',
                        borderRadius: '8px',
                        px: { xs: 2, md: 1.5 },
                        py: { xs: 1.5, md: 0.9 },
                        fontSize: { xs: '1rem', md: '0.9rem' },
                        textTransform: 'none',
                        fontWeight: active ? 600 : 500,
                        color: active ? brand.white : alpha(brand.white, 0.82),
                        backgroundColor: active ? alpha(TRACE_BRAND_SKY, 0.2) : 'transparent',
                        minHeight: { xs: 44, md: 36 },
                        '&:hover': {
                          backgroundColor: alpha(TRACE_BRAND_SKY, 0.14),
                          borderColor: alpha(TRACE_BRAND_SKY, 0.26),
                        },
                        '&.active': {
                          color: brand.white,
                          backgroundColor: alpha(TRACE_BRAND_SKY, 0.2),
                          borderColor: alpha(TRACE_BRAND_SKY, 0.42),
                        },
                      }}
                    >
                      {t(item.translationKey)}
                    </NavButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Drawer>

      {/* Top AppBar */}
      <StyledAppBar elevation={0} open={sidebarOpen && !isMobile}>
        <Toolbar disableGutters>
          {/* Sidebar Toggle */}
          <IconButton
            color="inherit"
            onClick={handleSidebarToggle}
            aria-label="toggle sidebar"
            edge="start"
            size={isMobile ? 'medium' : 'small'}
            sx={{ ml: 0.5, color: 'primary.main', '&:hover': { color: TRACE_BRAND_NAVY, backgroundColor: alpha(TRACE_BRAND_NAVY, 0.06) } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageSwitcher />

            {user ? (
              <>
                {/* User Avatar/Profile */}
                <Box
                  onClick={handleMenuOpen}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: { xs: '6px', sm: '7px 10px' },
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(TRACE_BRAND_NAVY, 0.06),
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 36, sm: 38 },
                      height: { xs: 36, sm: 38 },
                      bgcolor: 'primary.main',
                      fontSize: '1rem',
                      fontWeight: 700,
                      border: '2px solid',
                      borderColor: brand.sky,
                      boxShadow: 'none',
                    }}
                    title={`${user.name || user.username} (${user.role || 'User'})`}
                  >
                    {user.name ? user.name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ ml: 1.5, display: { xs: 'none', sm: 'block' } }}>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: 600, fontSize: '0.9rem' }}>
                      {user.name || user.username}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                      {user.role || 'User'}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                Login
              </Button>
            )}
          </Box>

          {user && (
            <UserMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'primary.main',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      border: '2px solid',
                      borderColor: 'primary.light',
                    }}
                  >
                    {user.name ? user.name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {user.name || user.username}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                      {user.role || 'User'}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  {user.email || `${user.username}@trace.local`}
                </Typography>
              </Box>
              <Divider />
              <MenuItem component={Link} to="/profile" onClick={handleClose}>
                <Person fontSize="small" sx={{ mr: 1.5 }} />
                {t('nav.profile')}
              </MenuItem>
              <MenuItem component={Link} to="/help" onClick={handleClose}>
                <HelpIcon fontSize="small" sx={{ mr: 1.5 }} />
                {t('nav.help')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ExitToApp fontSize="small" sx={{ mr: 1.5 }} />
                Uitloggen
              </MenuItem>
            </UserMenu>
          )}
        </Toolbar>
      </StyledAppBar>
    </>
  );
}

// Export DRAWER_WIDTH for use in other components
export { DRAWER_WIDTH };
export default Navbar;
